import React, { useState, useEffect } from 'react';
import { apiService, BlogCategory } from '../services/api';
import { Plus, Edit, Trash2, Hash, Search, CheckCircle2, XCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const BlogCategories: React.FC = () => {
    const { showToast } = useToast();
    const [categories, setCategories] = useState<BlogCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState<BlogCategory | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const data = await apiService.getBlogCategories();
            setCategories(data || []);
        } catch (error) {
            console.error('Failed to fetch blog categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);

        // Normalize boolean
        if (fd.has('is_active')) {
            const val = fd.get('is_active');
            fd.set('is_active', val === 'on' || val === 'true' ? 'true' : 'false');
        } else {
            fd.append('is_active', 'false');
        }

        try {
            if (editingCategory) {
                await apiService.updateBlogCategory(editingCategory.slug, fd);
            } else {
                await apiService.createBlogCategory(fd);
            }

            setShowForm(false);
            setEditingCategory(null);
            showToast('Category saved successfully!', 'success');
            fetchCategories();
        } catch (error) {
            console.error('Failed to save category:', error);
            showToast('Error saving category.', 'error');
        }
    };

    const handleDelete = async (slug: string) => {
        if (window.confirm('Are you sure you want to delete this category? This might affect blog posts linked to it.')) {
            try {
                await apiService.deleteBlogCategory(slug);
                showToast('Category deleted successfully!', 'success');
                fetchCategories();
            } catch (error) {
                console.error('Failed to delete category:', error);
                showToast('Failed to delete category.', 'error');
            }
        }
    };

    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Blog Categories</h1>
                    <p className="text-gray-600">Categorize your articles for better navigation</p>
                </div>
                <button
                    onClick={() => { setEditingCategory(null); setShowForm(true); }}
                    className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-bold"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    New Category
                </button>
            </div>

            <div className="mb-6 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Filter categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500/20 outline-none"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCategories.map((cat) => (
                    <div key={cat.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col group hover:border-orange-200 transition-colors">
                        <div className="flex justify-between items-start mb-4">
                            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                <Hash className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                <button onClick={() => { setEditingCategory(cat); setShowForm(true); }} className="p-2 text-gray-400 hover:text-orange-600 bg-gray-50 rounded-lg"><Edit className="h-4 w-4" /></button>
                                <button onClick={() => handleDelete(cat.slug)} className="p-2 text-gray-400 hover:text-red-600 bg-gray-50 rounded-lg"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 truncate">{cat.name}</h3>
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">{cat.description || 'No description provided.'}</p>
                        <div className="mt-4 flex items-center gap-2">
                            {cat.is_active ? (
                                <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full uppercase">
                                    <CheckCircle2 className="h-3 w-3" /> Active
                                </span>
                            ) : (
                                <span className="flex items-center gap-1 text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full uppercase">
                                    <XCircle className="h-3 w-3" /> Inactive
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
                        <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="text-xl font-bold text-gray-900">
                                {editingCategory ? 'Edit Category' : 'Create Category'}
                            </h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Category Name</label>
                                <input name="name" defaultValue={editingCategory?.name} required className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition" />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                                <textarea name="description" defaultValue={editingCategory?.description} rows={3} className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition" />
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="is_active" id="blog_cat_active" defaultChecked={editingCategory?.is_active !== false} className="h-5 w-5 text-orange-600 rounded-lg" />
                                <label htmlFor="blog_cat_active" className="text-sm font-bold text-gray-700">Display Category</label>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 text-gray-500 font-bold hover:text-gray-700">Cancel</button>
                                <button type="submit" className="px-10 py-3 bg-orange-600 text-white rounded-xl font-bold hover:bg-orange-700 transition shadow-lg shadow-orange-100">
                                    {editingCategory ? 'Save Changes' : 'Create Category'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {filteredCategories.length === 0 && (
                <div className="text-center py-24 bg-gray-50 rounded-2xl border border-gray-100 mt-8">
                    <Hash className="mx-auto h-12 w-12 text-gray-200 mb-4" />
                    <p className="text-gray-500 font-medium">No blog categories found.</p>
                </div>
            )}
        </div>
    );
};

export default BlogCategories;
